import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseCreateDailyTempWorkerMutation, UseDailyTempWorkerMutation } from '../../../../hooks/mutations/useDailyTempWorkerMutation';
import { useTempWorkersQuery } from '../../../../hooks/queries/useTempWorkersQuery';
import { DailyTempWorker } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getMaterialStateInfos, materialState, systemConfigKeyEnum } from '../../../../utils/enums';
import DropDown, { OptionType } from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { InputAdornment } from '@mui/material';
import IconButton from '../../../shared/IconButton';
import QuantityInput from '../../../QuantityInput';

interface ManageValidationDailyTempWorkerModalProps extends ModalProps {
    refetch: () => void;
    dailyTempWorker: DailyTempWorker | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyTempWorkerModal: React.FC<ManageValidationDailyTempWorkerModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyTempWorker,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyTempWorkerId] = useState(dailyTempWorker?._id || '');
    const [unitPrice, setUnitPrice] = useState(dailyTempWorker?.unitPrice?.toString() || '');
    const [tempWorkerName, setTempWorkerName] = useState(dailyTempWorker?.tempWorkerName || '');
    const [hours, setHours] = useState(dailyTempWorker?.durationHours?.toString() || '7');
    const [selectedTempWorkerType, setSelectedTempWorkerType] = useState({
        label: dailyTempWorker?.tempWorkerTypeId.designation || '',
        value: dailyTempWorker?.tempWorkerTypeId._id || '',
    });
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailyTempWorker?.activityId)?.label || '',
        value: dailyTempWorker?.activityId || '',
    });
    //const [quantity, setQuantity] = useState<number>(dailyTempWorker?.durationHours ?? 7);

    const { data: dataTempWorkers } = useTempWorkersQuery({ queries: { active: true } });

    const { mutate: createDailyTempWorker } = UseCreateDailyTempWorkerMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyTempWorker } = UseDailyTempWorkerMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailyTempWorker = async () => {
        createDailyTempWorker({
            body: {
                activityId: selectedActivity.value,
                tempWorkerTypeId: selectedTempWorkerType.value,
                tempWorkerName,
                durationHours: parseFloat(hours),
                unitPrice: unitPrice.length ? parseFloat(unitPrice) : undefined,
            },
        });
    };

    const patchDailyTempWorker = async () => {
        if (currentDailyTempWorkerId)
            updateDailyTempWorker({
                id: currentDailyTempWorkerId,
                body: {
                    activityId: selectedActivity.value,
                    tempWorkerTypeId: selectedTempWorkerType.value,
                    tempWorkerName,
                    durationHours: parseFloat(hours),
                    unitPrice: unitPrice.length ? parseFloat(unitPrice) : null,
                },
            });
    };

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('tempWorker')}
            subTitle={!!currentDailyTempWorkerId.length ? t('update') : t('add')}
            description={siteName}
            okText={t('validate')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentDailyTempWorkerId.length ? postDailyTempWorker() : patchDailyTempWorker();
            }}
            disabledOk={!selectedTempWorkerType.value.length || !selectedActivity.value.length || !tempWorkerName.length || !hours.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedTempWorkerType}
                items={
                    dataTempWorkers?.map((tempWorker) => ({
                        label: tempWorker.designation,
                        value: tempWorker._id,
                    })) || []
                }
                handleChange={(event, value) => setSelectedTempWorkerType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('company')}
                required
            />
            <CustomTextInput value={tempWorkerName} onChange={(value) => setTempWorkerName(value)} label={t('name')} required />
            <CustomTextInput
                label={capitalizeFirstLetter(t('durationHours'))}
                type="number"
                value={hours}
                onChange={(text) => setHours(text)}
                required
                startAdornment={
                    <InputAdornment position="start">
                        <IconButton
                            iconName="remove"
                            disabled={parseFloat(hours) <= 0}
                            onClick={() => setHours(Math.max(0, (parseFloat(hours) || 0.5) - 0.5).toString())}
                        />
                    </InputAdornment>
                }
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            iconName="add"
                            disabled={parseFloat(hours) >= 24}
                            onClick={() => setHours(Math.min(24, (parseFloat(hours) || 0) + 0.5).toString())}
                        />
                    </InputAdornment>
                }
            />
            {/*<QuantityInput
                value={dailyTempWorker.durationHours ?? 0}
                onChange={(newValue) => updateDailyTempWorker({ id: dailyTempWorker._id, body: { durationHours: newValue } })}
                unit="H"
                min={0}
                max={24}
                step={0.5}
                color="success"
                size="small"
            />*/}

            <CustomTextInput
                value={unitPrice}
                onChange={setUnitPrice}
                label={t('unitPrice')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
        </ModalBody>
    );
};

export default ManageValidationDailyTempWorkerModal;
